import type { Locale } from "date-fns"
import * as dateFnsLocale from "date-fns/locale"

export interface LanguageMetadata {
  label: string
  pluralize: (n: number) => number
  rtl?: boolean
  datepickerLocale?: Locale
}

export const languageMetadata: Record<string, LanguageMetadata> = {
  "en-US": {
    label: "English (US)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.enUS,
  },
  "en-GB": {
    label: "English (UK)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.enGB,
  },
  "en-AU": {
    label: "English (AU)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.enAU,
  },
  "en-CA": {
    label: "English (Canada)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.enCA,
  },
  "en-IN": {
    label: "English (India)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.enIN,
  },
  "es-ES": {
    label: "Español (España)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.es,
  },
  "es-MX": {
    label: "Español (México)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.es,
  },
  "es-AR": {
    label: "Español (Argentina)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.ar,
  },
  "es-CO": {
    label: "Español (Colombia)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.es,
  },
  "fr-FR": {
    label: "Français (France)",
    pluralize: (n: number) => n <= 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.fr,
  },
  "fr-CA": {
    label: "Français (Canada)",
    pluralize: (n: number) => n <= 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.frCA,
  },
  "fr-BE": {
    label: "Français (Belgique)",
    pluralize: (n: number) => n <= 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.fr,
  },
  "de-DE": {
    label: "Deutsch (Deutschland)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.de,
  },
  "de-AT": {
    label: "Deutsch (Österreich)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.deAT,
  },
  "de-CH": {
    label: "Deutsch (Schweiz)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.de,
  },
  "it-IT": {
    label: "Italiano (Italia)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.it,
  },
  "pt-PT": {
    label: "Português (Portugal)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.pt,
  },
  "pt-BR": {
    label: "Português (Brasil)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.ptBR,
  },
  "ru-RU": {
    label: "Русский (Россия)",
    pluralize: (n: number) => {
      const mod10 = n % 10
      const mod100 = n % 100
      if (mod10 === 1 && mod100 !== 11)
        return 0
      if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100))
        return 1
      return 2
    },
    datepickerLocale: dateFnsLocale.ru,
  },
  "zh-CN": {
    label: "中文 (简体)",
    pluralize: () => 0,
    datepickerLocale: dateFnsLocale.zhCN,
  },
  "zh-TW": {
    label: "中文 (繁體)",
    pluralize: () => 0,
    datepickerLocale: dateFnsLocale.zhTW,
  },
  "ja-JP": {
    label: "日本語 (日本)",
    pluralize: () => 0,
    datepickerLocale: dateFnsLocale.ja,
  },
  "ko-KR": {
    label: "한국어 (대한민국)",
    pluralize: () => 0,
    datepickerLocale: dateFnsLocale.ko,
  },
  "ar-SA": {
    label: "العربية (السعودية)",
    pluralize: (n: number) => {
      if (n === 0)
        return 0
      if (n === 1)
        return 1
      if (n === 2)
        return 2
      if (n % 100 >= 3 && n % 100 <= 10)
        return 3
      if (n % 100 >= 11)
        return 4
      return 5
    },
    rtl: true,
    datepickerLocale: dateFnsLocale.arSA,
  },
  "ar-EG": {
    label: "العربية (مصر)",
    pluralize: (n: number) => {
      if (n === 0)
        return 0
      if (n === 1)
        return 1
      if (n === 2)
        return 2
      if (n % 100 >= 3 && n % 100 <= 10)
        return 3
      if (n % 100 >= 11)
        return 4
      return 5
    },
    rtl: true,
    datepickerLocale: dateFnsLocale.arEG,
  },
  "nl-NL": {
    label: "Nederlands (Nederland)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.nl,
  },
  "nl-BE": {
    label: "Nederlands (België)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.nlBE,
  },
  "sv-SE": {
    label: "Svenska (Sverige)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.sv,
  },
  "no-NO": {
    label: "Norsk (Norge)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
  },
  "da-DK": {
    label: "Dansk (Danmark)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.da,
  },
  "fi-FI": {
    label: "Suomi (Suomi)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.fi,
  },
  "pl-PL": {
    label: "Polski (Polska)",
    pluralize: (n: number) => {
      if (n === 1)
        return 0
      if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100))
        return 1
      return 2
    },
    datepickerLocale: dateFnsLocale.pl,
  },
  "tr-TR": {
    label: "Türkçe (Türkiye)",
    pluralize: () => 0,
    datepickerLocale: dateFnsLocale.tr,
  },
  "cs-CZ": {
    label: "Čeština (Česko)",
    pluralize: (n: number) => {
      if (n === 1)
        return 0
      if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100))
        return 1
      return 2
    },
    datepickerLocale: dateFnsLocale.cs,
  },
  "el-GR": {
    label: "Ελληνικά (Ελλάδα)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.el,
  },
  "he-IL": {
    label: "עברית (ישראל)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    rtl: true,
    datepickerLocale: dateFnsLocale.he,
  },
  "hu-HU": {
    label: "Magyar (Magyarország)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.hu,
  },
  "ro-RO": {
    label: "Română (România)",
    pluralize: (n: number) => {
      if (n === 1)
        return 0
      if (n === 0 || (n % 100 >= 2 && n % 100 <= 19))
        return 1
      return 2
    },
    datepickerLocale: dateFnsLocale.ro,
  },
  "sk-SK": {
    label: "Slovenčina (Slovensko)",
    pluralize: (n: number) => {
      if (n === 1)
        return 0
      if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100))
        return 1
      return 2
    },
    datepickerLocale: dateFnsLocale.sk,
  },
  "uk-UA": {
    label: "Українська (Україна)",
    pluralize: (n: number) => {
      const mod10 = n % 10
      const mod100 = n % 100
      if (mod10 === 1 && mod100 !== 11)
        return 0
      if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100))
        return 1
      return 2
    },
    datepickerLocale: dateFnsLocale.uk,
  },
  "bg-BG": {
    label: "Български (България)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.bg,
  },
  "hr-HR": {
    label: "Hrvatski (Hrvatska)",
    pluralize: (n: number) => {
      if (n % 10 === 1 && n % 100 !== 11)
        return 0
      if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100))
        return 1
      return 2
    },
    datepickerLocale: dateFnsLocale.hr,
  },
  "sr-RS": {
    label: "Српски (Србија)",
    pluralize: (n: number) => {
      if (n % 10 === 1 && n % 100 !== 11)
        return 0
      if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100))
        return 1
      return 2
    },
    datepickerLocale: dateFnsLocale.sr,
  },
  "sl-SI": {
    label: "Slovenščina (Slovenija)",
    pluralize: (n: number) => {
      if (n % 100 === 1)
        return 0
      if (n % 100 === 2)
        return 1
      if (n % 100 === 3 || n % 100 === 4)
        return 2
      return 3
    },
    datepickerLocale: dateFnsLocale.sl,
  },
  "th-TH": {
    label: "ไทย (ประเทศไทย)",
    pluralize: () => 0,
    datepickerLocale: dateFnsLocale.th,
  },
  "vi-VN": {
    label: "Tiếng Việt (Việt Nam)",
    pluralize: () => 0,
    datepickerLocale: dateFnsLocale.vi,
  },
  "id-ID": {
    label: "Bahasa Indonesia (Indonesia)",
    pluralize: () => 0,
    datepickerLocale: dateFnsLocale.id,
  },
  "ms-MY": {
    label: "Bahasa Melayu (Malaysia)",
    pluralize: () => 0,
    datepickerLocale: dateFnsLocale.ms,
  },
  "hi-IN": {
    label: "हिन्दी (भारत)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.hi,
  },
  "ta-IN": {
    label: "தமிழ் (இந்தியா)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.ta,
  },
  "te-IN": {
    label: "తెలుగు (భారతదేశం)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.te,
  },
  "ml-IN": {
    label: "മലയാളം (ഇന്ത്യ)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
  },
  "bn-BD": {
    label: "বাংলা (বাংলাদেশ)",
    pluralize: (n: number) => n === 1 ? 0 : 1,
    datepickerLocale: dateFnsLocale.bn,
  },
  "fa-IR": {
    label: "فارسی (ایران)",
    pluralize: () => 0,
    rtl: true,
    datepickerLocale: dateFnsLocale.faIR,
  },
  "ur-PK": {
    label: "اردو (پاکستان)",
    pluralize: () => 0,
    rtl: true,
  },
}

/**
 * Language labels are used to display the language name in the language selector.
 */
export const languageLabels: Record<string, string> = Object.fromEntries(
  Object.entries(languageMetadata).map(([code, meta]) => [code, meta.label]),
)

/**
 * Datepicker locales are used to format the date in the datepicker.
 * If a language is not supported, the default locale is enUS.
 */
export const datepickerLocales: Record<string, Locale> = Object.fromEntries(
  Object.entries(languageMetadata).map(([code, meta]) => [code, meta?.datepickerLocale ?? dateFnsLocale.enUS]),
)
